import React, { useCallback, useState } from "react";
import {
  ArrowRefreshIcon,
  CancelCrossIcon,
  Caption1,
  DriveIcon,
  DropboxIcon,
  Tooltip,
  SpinnerLoader,
  IconButton,
  SkeletonLoader,
} from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";

export const SelectAttachmentsItem = ({
  id,
  name,
  type,
  thumbnailUrl: url,
  uploading,
  progress,
  error,
  onRemove,
  onRetry,
  className,
  size,
  disableTooltip,
  analyzing,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const handleRemove = useCallback(() => {
    onRemove(id);
  }, [id, onRemove]);

  const handleRetry = useCallback(() => {
    onRetry(id);
  }, [id, onRetry]);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  if (type === "google" || type === "dropbox") {
    return (
      <Tooltip
        title={name}
        disable={uploading || error || disableTooltip}
      >
        <div
          className={classnames("tw-border tw-border-solid tw-rounded-md tw-relative google-dropbox-border", {
            "tw-flex tw-items-center tw-justify-center": !error & !uploading,
          })}
          style={{
            width: size,
            height: size,
          }}
        >
          {typeof onRemove === "function" && (
            <IconButton
              type="button"
              className="tw-absolute tw-text-center tw-bg-black tw-text-white tw-rounded-full tw--top-[5px] tw--right-[5px] tw-border-none tw-w-5 tw-h-5"
              variant="tertiary"
              onClick={handleRemove}
            >
              <CancelCrossIcon className="tw-h-3 tw-w-3" />
            </IconButton>
          )}
          {type === "google" ? (
            <DriveIcon className="tw-h-10 tw-w-10 attachment-drive-icon-color" />
          ) : (
            <DropboxIcon className="tw-h-10 tw-w-10 attachment-dropbox-icon-color" />
          )}
        </div>
      </Tooltip>
    );
  }

  const showSpinner = uploading && progress === 100;

  return (
    <Tooltip
      title={name}
      disable={uploading || error || disableTooltip}
    >
      <div
        className={classnames(
          "tw-relative",
          {
            "tw-p-1": uploading,
            "tw-rounded-md tw-border tw-border-solid": !analyzing,
            "tw-flex tw-items-center tw-justify-center error-border": error & !uploading && !analyzing,

            "computer-border": !error,
            "ai-border": analyzing,
          },
          className
        )}
        style={{
          width: size,
          height: size,
        }}
      >
        {analyzing ? <SkeletonLoader className="tw-absolute tw-w-full tw-h-full" /> : null}
        {error ? (
          <IconButton
            type="button"
            variant="outlined"
            className="tw-border-none"
            onClick={handleRetry}
          >
            <ArrowRefreshIcon />
          </IconButton>
        ) : !uploading || showSpinner ? (
          <>
            <div
              className={classnames("tw-w-full tw-h-full tw-overflow-hidden tw-rounded-md", {
                "tw-hidden": isLoading,
              })}
            >
              <img
                src={url}
                alt={name}
                onLoad={handleLoad}
                className="tw-h-full tw-w-full tw-object-cover"
              />
            </div>
            <div
              className={classnames("tw-flex tw-items-center tw-justify-center tw-h-full", { "tw-hidden": !isLoading })}
            >
              <SpinnerLoader activeStrokeColor="var(--color-primary)" />
            </div>
          </>
        ) : (
          <div className="tw-text-center tw-h-full tw-flex tw-flex-col">
            <Caption1
              weight="bold"
              align="center"
              className="tw-flex-grow tw-flex tw-flex-col tw-justify-center"
            >{`${progress}%`}</Caption1>
            <div className="tw-h-1 tw-rounded-3xl tw-mt-auto progress-bar-container">
              <div
                className="tw-h-1 tw-bg-primary tw-rounded-tl-3xl tw-rounded-bl-3xl"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
        {typeof onRemove === "function" && (
          <IconButton
            type="button"
            className="tw-absolute tw-text-center tw-bg-black tw-text-white tw-rounded-full tw--top-[5px] tw--right-[5px] tw-border-none tw-w-5 tw-h-5"
            variant="tertiary"
            onClick={handleRemove}
          >
            <CancelCrossIcon className="tw-h-3 tw-w-3" />
          </IconButton>
        )}
      </div>
    </Tooltip>
  );
};

SelectAttachmentsItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["google", "dropbox", "local", "warehouse"]).isRequired,
  thumbnailUrl: PropTypes.string,
  uploading: PropTypes.bool,
  progress: PropTypes.number,
  error: PropTypes.bool,
  onRemove: PropTypes.func,
  onRetry: PropTypes.func,
  className: PropTypes.string,
  size: PropTypes.number,
  disableTooltip: PropTypes.bool,
  analyzing: PropTypes.bool,
};

SelectAttachmentsItem.defaultProps = {
  uploading: false,
  progress: 0,
  error: false,
  size: 60,
  onRetry: () => null,
};
